import { useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../services/api";
import { SocketContext } from "../context/Socket/SocketContext";

const isSnapshot = (value) => value?.enabled === false ||
  (value?.enabled === true && ["FULL", "BILLING_ONLY"].includes(value.accessMode));

export default function useBillingStatus({ isAuth, user }) {
  const socketManager = useContext(SocketContext);
  const companyId = user?.companyId;
  const key = isAuth && companyId ? `${companyId}:${user?.id || ""}` : null;
  const currentKey = useRef(key);
  currentKey.current = key;
  const mounted = useRef(true);
  const inFlight = useRef(null);
  const requestVersion = useRef(0);
  const [state, setState] = useState({ key: null, billing: null, error: false });

  const refreshBilling = useCallback(async () => {
    if (!key) return null;
    if (inFlight.current?.key === key) return inFlight.current.promise;
    const request = { key, promise: null };
    const version = requestVersion.current;
    request.promise = (async () => {
      try {
        const { data } = await api.get("/billing/status");
        if (!isSnapshot(data)) throw new Error("Invalid billing status");
        if (mounted.current && currentKey.current === key && requestVersion.current === version) {
          setState({ key, billing: data, error: false });
        }
        return data;
      } catch (error) {
        if (mounted.current && currentKey.current === key && requestVersion.current === version) {
          setState(previous => ({
            key,
            billing: error?.response?.status === 404 ? { enabled: false } : previous.key === key ? previous.billing : null,
            error: error?.response?.status !== 404
          }));
        }
        return null;
      } finally {
        if (inFlight.current === request) inFlight.current = null;
      }
    })();
    inFlight.current = request;
    return request.promise;
  }, [key]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!key) {
      setState({ key: null, billing: null, error: false });
      return undefined;
    }
    refreshBilling();
    const refreshWhenVisible = () => {
      if (document.visibilityState !== "hidden") refreshBilling();
    };
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("online", refreshWhenVisible);
    window.addEventListener("pageshow", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    const timer = setInterval(refreshWhenVisible, 60000);
    return () => {
      requestVersion.current += 1;
      inFlight.current = null;
      clearInterval(timer);
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("online", refreshWhenVisible);
      window.removeEventListener("pageshow", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [key, refreshBilling]);

  useEffect(() => {
    if (!key || !socketManager?.getSocket) return undefined;
    const socket = socketManager.getSocket(companyId);
    const event = `company-${companyId}-billing`;
    // Fetch canonical state; a delayed socket payload must never grant access.
    const refresh = () => refreshBilling();
    socket.on(event, refresh);
    socket.on("ready", refresh);
    return () => {
      socket.off(event, refresh);
      socket.off("ready", refresh);
    };
  }, [key, companyId, socketManager, refreshBilling]);

  useEffect(() => {
    if (!key) return undefined;
    const interceptor = api.interceptors.response.use(undefined, error => {
      if (mounted.current && currentKey.current === key && error?.response?.status === 403 && error?.response?.data?.error === "ERR_COMPANY_ACCESS_RESTRICTED" && !error?.config?.url?.includes("/billing/")) {
        requestVersion.current += 1;
        inFlight.current = null;
        setState(previous => ({
          key,
          billing: { ...(previous.key === key ? previous.billing : {}), enabled: true, accessMode: "BILLING_ONLY" },
          error: false
        }));
        refreshBilling();
      }
      return Promise.reject(error);
    });
    return () => api.interceptors.response.eject(interceptor);
  }, [key, refreshBilling]);

  return {
    billing: key && state.key === key ? state.billing : null,
    billingLoading: Boolean(key && state.key !== key),
    billingError: Boolean(key && state.key === key && state.error),
    refreshBilling
  };
}
